import { renderAsync as renderDocx } from 'docx-preview';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import Loader from 'src/components/loader';
import useMediaQuery from 'src/hooks/useMediaQuery';

const DocxViewer = ({ url }) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMobileDevice = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    let isCancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch DOCX');
        const contentType = response.headers.get('content-type') || '';
        if (
          !/application\/(vnd\.openxmlformats-officedocument\.wordprocessingml\.document|zip)/i.test(
            contentType
          )
        ) {
          throw new Error(`Unexpected content-type: ${contentType || 'unknown'}`);
        }
        const buffer = await response.arrayBuffer();
        if (!isCancelled && containerRef.current) {
          containerRef.current.innerHTML = '';
          const renderOptions = {
            inWrapper: false,
            useBase64URL: true,
            breakPages: false,
            className: 'docx-wrapper',
            ignoreWidth: isMobileDevice,
            ignoreHeight: isMobileDevice,
            renderEndnotes: true,
            renderFootnotes: true,
            renderHeaders: true,
            renderFooters: true,
            debug: false,
            ...(isMobileDevice && {
              ignoreFonts: false,
              trimXmlDeclaration: true,
            }),
          };
          await renderDocx(buffer, containerRef.current, undefined, renderOptions);

          // Ensure layout is fully applied before hiding the loader to prevent blink on mobile
          await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        }
      } catch (error_) {
        if (!isCancelled) setError(error_);
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };
    load();
    return () => {
      isCancelled = true;
      if (containerRef.current) containerRef.current.innerHTML = '';
    };
  }, [url, isMobileDevice]);

  return (
    <div className='dv-docx-container'>
      {loading && (
        <div className='dv-loading'>
          <Loader showText={false} />
        </div>
      )}
      {error && (
        <div className='dv-error'>
          <div className='mb-2'>Failed to load DOCX file.</div>
          <a href={url} target='_blank' rel='noreferrer' className='redirect-link'>
            Open in new tab
          </a>
        </div>
      )}
      <div
        ref={containerRef}
        className='dv-docx-content'
        style={{ visibility: loading ? 'hidden' : 'visible' }}
      />
    </div>
  );
};

DocxViewer.propTypes = { url: PropTypes.string.isRequired };

export default DocxViewer;
